/**
 * NearbyChargersSection.tsx — T-497 (Noreen item 10: "add some of the data for nearby chargers that
 * is in pdf to 'view all metrics'").
 *
 * The PDF one-pager already computes a rich nearby-chargers block (NearbyStats: stations, DC-fast,
 * ports at concentric radii). This surfaces a compact subset of that SAME data in the in-app
 * view-all-metrics panel, by reusing the PDF's exact fetchNearbyStats helper — one source of truth,
 * no second gather. The ring radii (0.5 / 1.0 mi) are the column headers, so this block is
 * self-labeling: it states its own "nearby" distances.
 *
 * The POIs come from the site-score payload (score.ts's supplemental gather), like the export; only
 * the stations are fetched here. Renders nothing without a site pin, or when the lookup yields no
 * data — the same honest degradation the PDF uses.
 */
import { useEffect, useState } from 'react'
import { fetchNearbyStats } from '../lib/pdf/fetchNearby'
import type { NearbyStats, NearbyRing } from '../lib/nearbyStats'
import type { SiteScoreResult } from '../lib/tractModel'

export interface NearbyChargersSectionProps {
  /** The searched site (pin) lat/lng — the origin distances are measured from. Null → no block. */
  site: { lat: number; lng: number } | null
  siteScore?: SiteScoreResult | null
}

// 0.5 → "0.5 mi", 1 → "1.0 mi". Matches the PDF's miLabel so both surfaces read identically.
const miLabel = (mi: number): string => `${mi.toFixed(1)} mi`

interface Row {
  label: string
  pick: (r: NearbyRing) => number
}
const ROWS: Row[] = [
  { label: 'Stations', pick: (r) => r.stations },
  { label: 'DC fast', pick: (r) => r.dcFastStations },
  { label: 'L2 ports', pick: (r) => r.l2Ports },
  { label: 'Total ports', pick: (r) => r.totalPorts },
  { label: 'Stations w/ NACS', pick: (r) => r.nacsStations },
]

export function NearbyChargersSection({ site, siteScore }: NearbyChargersSectionProps) {
  const [stats, setStats] = useState<NearbyStats | null>(null)
  const [loading, setLoading] = useState(false)

  const lat = site?.lat ?? null
  const lng = site?.lng ?? null
  const nearbyPois = siteScore?.amenities?.nearbyPois
  const poiFetched = siteScore?.amenities?.poiFetched

  useEffect(() => {
    if (lat == null || lng == null || !nearbyPois) {
      setStats(null)
      return
    }
    let cancelled = false
    setLoading(true)
    fetchNearbyStats(lat, lng, nearbyPois, poiFetched ?? nearbyPois.length)
      .then((s) => {
        if (!cancelled) setStats(s)
      })
      .catch(() => {
        if (!cancelled) setStats(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [lat, lng, nearbyPois, poiFetched])

  // No pin (or no POIs to measure against) → the block has nothing honest to show.
  if (lat == null || lng == null || !nearbyPois) return null
  if (loading) {
    return <p className="mt-3 text-xs text-muted-foreground">Loading nearby chargers…</p>
  }
  if (!stats) return null

  const { rings, nearest, stationsAreFloor } = stats

  return (
    <div className="mt-3">
      <p className="text-xs text-muted-foreground mb-1">Nearby chargers:</p>
      <table className="w-full text-sm tabular-nums">
        <thead>
          <tr>
            <th className="text-left" />
            {rings.map((r) => (
              <th
                key={r.radiusMi}
                className="px-1 text-right text-xs font-normal text-muted-foreground"
              >
                {miLabel(r.radiusMi)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <tr key={row.label} className="border-b border-border/40 last:border-0">
              <td className="py-0.5 text-secondary-foreground">{row.label}</td>
              {rings.map((r) => (
                <td key={r.radiusMi} className="px-1 py-0.5 text-right font-bold text-foreground">
                  {row.pick(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {/* Prose the PDF also prints — a nearest-station fact so a table of zeroes never reads as
          "no competition anywhere". */}
      {nearest && (
        <p className="mt-1 text-[0.7rem] text-muted-foreground">
          Nearest: {nearest.network} — {nearest.name.trim() || 'unnamed station'} (
          {nearest.distanceMi.toFixed(2)} mi)
        </p>
      )}
      {/* Truth-in-labeling: a capped lookup makes the counts floors, and a floor printed as a count
          is a lie — say so, exactly like the one-pager footnote. */}
      {stationsAreFloor && (
        <p className="mt-0.5 text-[0.7rem] italic text-muted-foreground">
          Station counts are at least the numbers shown (lookup hit its result cap).
        </p>
      )}
    </div>
  )
}
